// Selections folder builder — modern icon-driven UI.
//
// Layout (vertical column inside the Selections folder body; ordering
// driven by CSS `order:` so the editor sub-folder slots between
// our custom DOM regions without being forced to the bottom of the column):
//
//   [□]  [○]  [≠]                  ← add row (3 square icons; criterion
//                                    is the disabled v2 stub). Always shown.
//   "Your selections"              ← list title + tab strip; both shown only
//   [□] [○] [□] [○] [○] [□] ▸        when ≥1 selection exists — when empty,
//                                    only the add row above appears.
//
//   Label  [Box A      ]           ← editor sub-folder for the
//   X min  [────●────  ]              focused selection only (sub-folder
//   …                                 title bar is hidden via CSS).
//
//   [◎ focus]  [🗑 delete]            ← two square action icons; disabled
//                                    when nothing is focused.
//
// Rebuild model:
//   - `buildSelectionsSection` ensures the static skeleton + does a full
//     render (use on selectionOrder changes — add/delete/dataset switch).
//   - `refreshSelectionFocusMarkers` is the light path: skips tab-strip
//     DOM regen, swaps tab `aria-selected`, retargets the header + editor
//     + action buttons. Returns false if refs are missing so callers fall
//     back to a full rebuild.

import { store, params as storeParams } from '@store/instance';
import { INTENTS } from '@store/intents';
import type { TPaneLike, TFolderLike, Refs } from '../types';
import { rangeFormat } from '../numberFormat';
import { bindSlider } from '../sliderTicks';
import { mountRangeControl } from '../rangeAdapter';
import type { BoxSelection, Selection, SelectionId, SphereSelection } from '@store/schema';
import type { SelectionPatch } from '@store/selections';

interface SelectionsCtx {
    params: Record<string, unknown>;
    grid: { xMin: number; xMax: number; yMin: number; yMax: number; zMin: number; zMax: number; resolutionX: number; resolutionY: number; resolutionZ: number };
}

interface SelectionCustomRefs {
    addRow: HTMLDivElement;
    listTitle: HTMLDivElement;
    tabsEl: HTMLDivElement;
    editorFolder: TFolderLike | null;
    /** Last grid passed to a full build. Cached so the focus-only refresh
     *  path can rebuild the editor without re-fetching from `viewer.grid`
     *  (which the orchestrator doesn't pass on focus changes). Stays valid
     *  across focus changes because the grid only moves on dataset/resize,
     *  which both trigger a full rebuild. */
    lastGrid: SelectionsCtx['grid'];
}

// Custom refs are keyed on the folder itself (a WeakMap) rather than on the
// `refs` bag passed into `buildSelectionsSection`. The orchestrator in
// `sections.ts` constructs a fresh refs bag every call and only copies
// `selectionsFolderRef` back into `paneState.refs`, so any extra keys we
// wrote there were silently discarded — which made every rebuild call
// `buildSkeleton` again and stamp a second add-row + tab strip into the
// same folder body. The folder element is stable across rebuilds, so a
// WeakMap keyed on it survives correctly.
const CUSTOM_REFS_BY_FOLDER = new WeakMap<TFolderLike, SelectionCustomRefs>();

const ICON_BY_KIND: Record<Selection['kind'], string> = { box: 'fa-cube', sphere: 'fa-circle' };
const LABEL_BY_KIND: Record<Selection['kind'], string> = { box: 'Box', sphere: 'Sphere' };

/** Per-axis step sizes for pane number bindings. Falls back to 0.1
 *  when the grid hasn't reported a resolution. */
function gridSteps(grid: SelectionsCtx['grid']): { stepX: number; stepY: number; stepZ: number; stepR: number } {
    const stepX = grid.resolutionX || 0.1;
    const stepY = grid.resolutionY || 0.1;
    const stepZ = grid.resolutionZ || 0.1;
    return { stepX, stepY, stepZ, stepR: Math.min(stepX, stepY, stepZ) || 0.1 };
}

function addLabelBinding<E extends { labelText: string }>(folder: TFolderLike, id: SelectionId, edit: E): void {
    folder.addBinding(edit, 'labelText', { label: 'Label' }).on('change', (ev) => {
        store.dispatch(INTENTS.SELECTION_UPDATE, { id, patch: { label: String(ev.value ?? '') } });
    });
}

interface AxisBinding<E extends object> {
    key: keyof E & string;
    label: string;
    min: number;
    max: number;
    step: number;
    patch: (value: number) => SelectionPatch;
}

function addAxisBindings<E extends object>(
    folder: TFolderLike,
    edit: E,
    id: SelectionId,
    rows: ReadonlyArray<AxisBinding<E>>,
): void {
    for (const row of rows) {
        bindSlider(folder, edit, row.key, { label: row.label, min: row.min, max: row.max, step: row.step, format: rangeFormat(row.min, row.max) })
              .on('change', (ev) => {
                  store.dispatch(INTENTS.SELECTION_UPDATE, { id, patch: row.patch(Number(ev.value)) });
              });
    }
}

function addBoxEditor(folder: TFolderLike, id: SelectionId, sel: BoxSelection, grid: SelectionsCtx['grid']): void {
    const edit = {
        labelText: sel.label,
        x: { min: sel.lower[0], max: sel.upper[0] },
        y: { min: sel.lower[1], max: sel.upper[1] },
        z: { min: sel.lower[2], max: sel.upper[2] },
    };
    addLabelBinding(folder, id, edit);

    // Three interval rows replace the six single-axis bindings. Each binding's
    // value is { min, max } and hosts a dual-grip RangeControl (dragging the
    // fill moves both ends); the other two axes flow through unchanged.
    const { stepX, stepY, stepZ } = gridSteps(grid);
    const bindInterval = (
        key: 'x' | 'y' | 'z',
        label: string,
        domainMin: number,
        domainMax: number,
        step: number,
        patch: (lo: number, hi: number) => SelectionPatch,
    ): void => {
        const b = folder.addBinding(edit, key, {
            view: 'interval', label, min: domainMin, max: domainMax, step, format: rangeFormat(domainMin, domainMax),
        });
        const dispatch = (lo: number, hi: number): void => {
            store.dispatch(INTENTS.SELECTION_UPDATE, { id, patch: patch(lo, hi) });
        };
        // Custom interval control unconditionally — dragging the fill moves both
        // ends together, which a two-knob slider can't do.
        const cur = edit[key];
        const ctrl = mountRangeControl(b, {
            min: domainMin, max: domainMax, step,
            range: [cur.min, cur.max],
            format: rangeFormat(domainMin, domainMax),
            onInput: (v) => { const [lo, hi] = v as [number, number]; dispatch(lo, hi); },
        });
        if (!ctrl) {
            b.on('change', (ev) => {
                const v = ev.value as { min: number; max: number };
                dispatch(v.min, v.max);
            });
        }
    };
    bindInterval('x', 'X', grid.xMin, grid.xMax, stepX,
        (lo, hi) => ({ lower: [lo, sel.lower[1], sel.lower[2]], upper: [hi, sel.upper[1], sel.upper[2]] }));
    bindInterval('y', 'Y', grid.yMin, grid.yMax, stepY,
        (lo, hi) => ({ lower: [sel.lower[0], lo, sel.lower[2]], upper: [sel.upper[0], hi, sel.upper[2]] }));
    bindInterval('z', 'Z', grid.zMin, grid.zMax, stepZ,
        (lo, hi) => ({ lower: [sel.lower[0], sel.lower[1], lo], upper: [sel.upper[0], sel.upper[1], hi] }));
}

function addSphereEditor(folder: TFolderLike, id: SelectionId, sel: SphereSelection, grid: SelectionsCtx['grid']): void {
    const ext = Math.max(grid.xMax - grid.xMin, grid.yMax - grid.yMin, grid.zMax - grid.zMin);
    const edit = {
        labelText: sel.label,
        centerX: sel.center[0], centerY: sel.center[1], centerZ: sel.center[2],
        radius: sel.radius,
        keep: sel.keep,
    };
    const { stepX, stepY, stepZ, stepR } = gridSteps(grid);
    addLabelBinding(folder, id, edit);
    addAxisBindings<typeof edit>(folder, edit, id, [
        { key: 'centerX', label: 'Center X', min: grid.xMin, max: grid.xMax, step: stepX, patch: (v) => ({ center: [v, edit.centerY, edit.centerZ] }) },
        { key: 'centerY', label: 'Center Y', min: grid.yMin, max: grid.yMax, step: stepY, patch: (v) => ({ center: [edit.centerX, v, edit.centerZ] }) },
        { key: 'centerZ', label: 'Center Z', min: grid.zMin, max: grid.zMax, step: stepZ, patch: (v) => ({ center: [edit.centerX, edit.centerY, v] }) },
        { key: 'radius',  label: 'Radius',   min: stepR,     max: ext,       step: stepR, patch: (v) => ({ radius: Math.max(1e-6, v) }) },
    ]);
    folder.addBinding(edit, 'keep', { label: 'Keep', options: { Inside: 'inside', Outside: 'outside' } }).on('change', (ev) => {
        store.dispatch(INTENTS.SELECTION_UPDATE, { id, patch: { keep: ev.value === 'outside' ? 'outside' : 'inside' } });
    });
}

/** Build an add-row button: icon glyph stacked over a short kind label
 *  ("Box", "Sphere", "Criterion"). Same shape as the selection tabs below
 *  so the two rows read as a matched family. */
function makeAddButton(icon: string, label: string, ariaLabel: string, opts?: { disabled?: boolean }): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = opts?.disabled ? 'selection-add is-disabled' : 'selection-add';
    btn.setAttribute('aria-label', ariaLabel);
    btn.title = ariaLabel;
    if (opts?.disabled) btn.setAttribute('aria-disabled', 'true');
    btn.innerHTML = `<i class="fas ${icon}" aria-hidden="true"></i>`
                  + `<span class="selection-add-label">${escapeAttr(label)}</span>`;
    return btn;
}

/** Build the static skeleton DOM (add row, tab strip, header, editor mount,
 *  action row, empty placeholder) inside the folder body. Called once per
 *  folder lifetime; subsequent rebuilds mutate the cached refs in place. */
function buildSkeleton(folder: TFolderLike): SelectionCustomRefs {
    const bodyEl = folder.element?.querySelector<HTMLElement>('.mz-fldv_c');
    if (!bodyEl) {
        // Should never happen — the pane always renders the body container.
        // Fall back to a detached fragment so the code keeps a stable shape.
        const dummy = document.createElement('div');
        return makeRefs(dummy);
    }
    const refs = makeRefs(bodyEl);
    return refs;
}

function makeRefs(parent: HTMLElement): SelectionCustomRefs {
    // Add row — three compact buttons stacked icon-over-label. Criterion is
    // the v2 stub. No section title (just the buttons themselves) — the
    // "Your selections" title below names what they create.
    const addRow = document.createElement('div');
    addRow.className = 'selection-add-row';
    addRow.setAttribute('role', 'group');
    addRow.setAttribute('aria-label', 'Add selection');
    const boxBtn = makeAddButton('fa-cube', 'Box', 'Add box selection');
    const sphereBtn = makeAddButton('fa-circle', 'Sphere', 'Add sphere selection');
    // `fa-superscript` reads as "x²" — a parameterized predicate glyph,
    // closer to "criterion / inequality / formula" than the literal ≠ was.
    const critBtn = makeAddButton('fa-superscript', 'Criterion',
        'Add criterion selection — coming in v2', { disabled: true });
    addRow.append(boxBtn, sphereBtn, critBtn);

    boxBtn.addEventListener('click', () => {
        store.dispatch(INTENTS.SELECTION_CREATE_BOX, {});
    });
    sphereBtn.addEventListener('click', () => {
        store.dispatch(INTENTS.SELECTION_CREATE_SPHERE, {});
    });
    // Match topbar's `onDisabledClick` convention for `.is-disabled`
    // placeholders — swallow the event so other handlers / focus moves
    // don't fire.
    critBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); });

    // Section title for the list below — labels the row so the user knows
    // the strip is the registry of created selections, not more add controls.
    const listTitle = document.createElement('div');
    listTitle.className = 'selection-list-title';
    listTitle.textContent = 'Your selections';

    // Tab strip — populated per-render. Click delegated to the container.
    // Two interactive children per tab-wrap: the tab button (focus / toggle
    // off if already focused) and the corner close button (delete). The
    // delegator distinguishes by `.closest()` and stops propagation on the
    // close hit so it doesn't also re-trigger focus on the surrounding tab.
    const tabsEl = document.createElement('div');
    tabsEl.className = 'selection-tabs';
    tabsEl.setAttribute('role', 'tablist');
    tabsEl.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const closeBtn = target.closest<HTMLElement>('.selection-tab-close');
        if (closeBtn) {
            e.stopPropagation();
            const id = closeBtn.dataset['selectionId'] as SelectionId | undefined;
            if (id) store.dispatch(INTENTS.SELECTION_DELETE, { id });
            return;
        }
        const btn = target.closest<HTMLElement>('.selection-tab');
        if (!btn) return;
        const id = btn.dataset['selectionId'] as SelectionId | undefined;
        if (!id) return;
        // Toggle behaviour: clicking the already-focused tab defocuses.
        // Resolves against the live store so it stays correct across
        // intermediate rebuilds.
        const nextId: SelectionId | null = storeParams.focusedSelectionId === id ? null : id;
        store.dispatch(INTENTS.SELECTION_FOCUS, { id: nextId });
    });

    parent.append(addRow, listTitle, tabsEl);

    const lastGrid: SelectionsCtx['grid'] = {
        xMin: 0, xMax: 1, yMin: 0, yMax: 1, zMin: 0, zMax: 1,
        resolutionX: 0.1, resolutionY: 0.1, resolutionZ: 0.1,
    };
    return { addRow, listTitle, tabsEl, editorFolder: null, lastGrid };
}

function renderTabs(refs: SelectionCustomRefs, order: ReadonlyArray<SelectionId>, focusedId: SelectionId | null): void {
    // Each tab is wrapped so the corner close button can sibling-position
    // absolutely without being nested inside the focus `<button>` (which
    // would be invalid HTML). Click delegation on the tabs container
    // distinguishes the two.
    refs.tabsEl.innerHTML = order.map((id) => {
        const sel = storeParams.selections[id];
        if (!sel) return '';
        const icon = ICON_BY_KIND[sel.kind];
        const isActive = id === focusedId;
        const labelEsc = escapeAttr(sel.label);
        const titleEsc = escapeAttr(`${LABEL_BY_KIND[sel.kind]}: ${sel.label}`);
        const deleteAria = escapeAttr(`Delete ${sel.label}`);
        return `<div class="selection-tab-wrap"${isActive ? ' data-active="true"' : ''}>`
             + `<button type="button" role="tab" class="selection-tab" `
             + `data-selection-id="${id}" `
             + `aria-selected="${isActive ? 'true' : 'false'}" `
             + `aria-label="${labelEsc}" title="${titleEsc}">`
             + `<i class="fas ${icon}" aria-hidden="true"></i>`
             + `<span class="selection-tab-label">${labelEsc}</span>`
             + `</button>`
             + `<button type="button" class="selection-tab-close" `
             + `data-selection-id="${id}" `
             + `aria-label="${deleteAria}" title="Delete">`
             + `&times;`
             + `</button>`
             + `</div>`;
    }).join('');
}

/** Dispose any existing editor sub-folder and (if a selection is focused)
 *  mount a new one with the bindings for that selection. */
function renderEditor(
    parentFolder: TFolderLike,
    refs: SelectionCustomRefs,
    focused: Selection | null,
    grid: SelectionsCtx['grid'],
): void {
    if (refs.editorFolder) {
        refs.editorFolder.dispose?.();
        refs.editorFolder = null;
    }
    if (!focused) return;
    const sub = parentFolder.addFolder?.({ title: ' ', expanded: true });
    if (!sub) return;
    if (sub.element) sub.element.dataset['role'] = 'selection-editor';
    if (focused.kind === 'box') addBoxEditor(sub, focused.id, focused, grid);
    else addSphereEditor(sub, focused.id, focused, grid);
    refs.editorFolder = sub;
}

function resolveFocused(focusedId: SelectionId | null): Selection | null {
    if (focusedId === null) return null;
    return storeParams.selections[focusedId] ?? null;
}

function escapeAttr(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function buildSelectionsSection(paneArg: unknown, refs: Refs, ctx: SelectionsCtx): Refs {
    const pane = paneArg as TPaneLike;
    const { grid } = ctx;
    let folder = refs['selectionsFolderRef'] as TFolderLike | undefined;
    if (!folder) {
        folder = pane.addFolder({ title: 'Selections', expanded: true });
        if (folder.element) folder.element.dataset['section'] = 'selection';
        refs['selectionsFolderRef'] = folder;
    }

    // Build the static custom-DOM skeleton on first call; reuse after.
    // The editor sub-folder is the only pane child of this folder
    // — it's recreated on every render via `renderEditor`.
    let customRefs = CUSTOM_REFS_BY_FOLDER.get(folder);
    if (!customRefs) {
        customRefs = buildSkeleton(folder);
        CUSTOM_REFS_BY_FOLDER.set(folder, customRefs);
    }

    const order = storeParams.selectionOrder;
    const focusedId = storeParams.focusedSelectionId;
    const focused = resolveFocused(focusedId);
    const effectiveFocusedId = focused ? focused.id : null;

    customRefs.lastGrid = grid;
    renderTabs(customRefs, order, effectiveFocusedId);
    renderEditor(folder, customRefs, focused, grid);
    // With no selections, show only the add row — hide the "Your selections"
    // title and the (empty) tab strip; the add buttons are self-labeling.
    const isEmpty = order.length === 0;
    customRefs.listTitle.hidden = isEmpty;
    customRefs.tabsEl.hidden = isEmpty;

    pane.refresh?.();
    return refs;
}

/**
 * Lightweight focus-only refresh. Updates `aria-selected` on the tab
 * strip, the header text, the action buttons, and rebuilds the editor
 * sub-folder for the newly-focused id. Returns `false` if the custom
 * refs are missing (caller should fall back to a full rebuild).
 *
 * Signature kept stable so the rebuild orchestrator in
 * `src/ui/panel/sections.ts` keeps working.
 */
export function refreshSelectionFocusMarkers(
    refs: Refs,
    _prevId: SelectionId | null,
    newId: SelectionId | null,
): boolean {
    void _prevId;
    const folder = refs['selectionsFolderRef'] as TFolderLike | undefined;
    if (!folder) return false;
    const customRefs = CUSTOM_REFS_BY_FOLDER.get(folder);
    if (!customRefs) return false;
    const grid = customRefs.lastGrid;
    const focused = resolveFocused(newId);
    customRefs.tabsEl.querySelectorAll<HTMLButtonElement>('.selection-tab').forEach((btn) => {
        const id = btn.dataset['selectionId'] as SelectionId | undefined;
        const isActive = id === newId;
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        const wrap = btn.parentElement;
        if (wrap?.classList.contains('selection-tab-wrap')) {
            if (isActive) wrap.dataset['active'] = 'true';
            else delete wrap.dataset['active'];
        }
    });
    renderEditor(folder, customRefs, focused, grid);
    return true;
}

